import { useAxios } from "auth-sdk";
import { UserDataSchemas } from "api-types";
import type { AxiosResponse } from "axios";
// eslint-disable-next-line no-restricted-imports
import moment from "moment";
import * as React from "react";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import { array, mixed, number, object, ObjectSchema, string } from "yup";

import type {
  Configuration as KycConfiguration,
  SupportedCountry as KycSupportedCountry,
  KycValidationSchema,
  PersonValidationSchema,
  SignerValidationByCountry,
} from "..";
import {
  companyIdValidationByCountry,
  companyVatValidationByCountry,
  idValidationByCountry,
  signerValidationByCountry,
} from "..";

export const STAFF_APP_USER_DATA_VALIDATION_URL = "/staff/user-data/api/validate";
export const CUSTOMER_APP_USER_DATA_VALIDATION_URL = "/:customerSlug/user-data/api/validate";

type PhoneNumberValidator = (
  number?: string,
  region?: string
) => Promise<AxiosResponse<UserDataSchemas.UserDataValidationResponse, any>>;

export const useUserDataValidation = ({ url }: { url: string }) => {
  const client = useAxios();

  return (data: UserDataSchemas.UserDataUpdate) =>
    client.post<UserDataSchemas.UserDataUpdate, AxiosResponse<UserDataSchemas.UserDataValidationResponse>>(url, data);
};

const ISO_SHORT_DATE_FORMAT = "YYYY-MM-DD";
const { localDateFormat, localDateFormatLowerCase } = {
  localDateFormat: ISO_SHORT_DATE_FORMAT,
  // NOTE: The order of the date parts is determined by the user's locale. This mimics the behavior of the
  // of the date input component from flyui
  localDateFormatLowerCase: (() => {
    // temp workaround for issue where navigator.language is returning en-US@posix when running tests
    const language = navigator.language || "en-US";
    const normalizedLanguage = language.split("@")?.[0] || "en-US";
    return new Intl.DateTimeFormat(normalizedLanguage)
      .formatToParts(new Date())
      .filter((part) => ["day", "month", "year"].includes(part.type))
      .map((part) => part.type)
      .join(", ")
      .replace("day", "dd")
      .replace("month", "mm")
      .replace("year", "yyyy");
  })(),
};

const usFiveDigitZipRegex = new RegExp(/(^\d{5}$)|(^\d{5}-\d{4}$)/);

const isValidIsoDate = (value: string) => !value || moment(value, ISO_SHORT_DATE_FORMAT).isValid();

const isOver16YearsOfAge = (value: string) =>
  moment(value, ISO_SHORT_DATE_FORMAT).isSameOrBefore(moment().subtract(16, "years"));

const isUnder140YearsOfAge = (value: string) =>
  moment(value, ISO_SHORT_DATE_FORMAT).isSameOrAfter(moment().subtract(140, "years"));

const getPostcodeValidation = (intl: IntlShape, countryField: string) => {
  return mixed().when(countryField, {
    is: "US",
    then: () =>
      string().matches(usFiveDigitZipRegex, {
        message: intl.formatMessage({
          id: "kyc: Please enter a valid 5-digit ZIP code",
          defaultMessage: "Please enter a valid 5-digit ZIP code",
        }),
      }),
    otherwise: () => string(),
  });
};

const getAddressValidation = (intl: IntlShape) => {
  const requiredFieldText: string = intl.formatMessage({
    id: "kyc: Required field",
    defaultMessage: "Required field",
  });
  const line1Validator = string();
  const line2Validator = string();
  const cityValidator = string();
  const stateValidator = string().when("company-info-reg-country", {
    is: "US",
    then: (schema) => schema.required(requiredFieldText),
    otherwise: (schema) => schema,
  });
  const countryValidator = string().required(requiredFieldText);
  return {
    line1Validator,
    line2Validator,
    cityValidator,
    stateValidator,
    countryValidator,
    postcodeValidator: getPostcodeValidation(intl, "company-info-reg-country"),
  };
};

export const kycValidator = (
  intl: IntlShape,
  country: string,
  phoneNumberValidator: PhoneNumberValidator
): ObjectSchema<KycValidationSchema> => {
  const addressValidation = getAddressValidation(intl);
  const requiredFieldText: string = intl.formatMessage({
    id: "kyc: Required field",
    defaultMessage: "Required field",
  });
  const identityNumberValidator = string().when(
    // N.B. Relies on the fact that we're adding a `kycConfiguration` field to the `useForm()`'s `context`
    "$kycConfiguration",
    ([kycConfiguration], schema) => {
      // TODO: with Yup v1.0 the `kycConfiguration` param will be given in an array
      if (!kycConfiguration) {
        return schema; //can happen during component setup - will well and truly be passed when the form is displayed
      }
      const countryCode = kycConfiguration.country;
      if (kycConfiguration.companyIdentification === false) {
        return schema;
      }
      if (kycConfiguration.companyIdentification.required) {
        schema = schema.required(requiredFieldText);
      }
      const validationUnit = companyIdValidationByCountry[countryCode];
      if (!validationUnit) {
        return schema;
      }
      return schema.test({
        name: `${countryCode}_EIN`,
        message: validationUnit.getMessage(intl),
        test: validationUnit.validate,
      });
    }
  );

  const vatNumberValidator = string().when("$kycConfiguration", ([kycConfiguration], schema) => {
    const countryCode = kycConfiguration?.country || country;

    const validationUnit = companyVatValidationByCountry(countryCode);
    if (!validationUnit) {
      return schema;
    }
    return schema.test({
      name: `${countryCode}_VAT`,
      message: validationUnit.getMessage(intl),
      test: validationUnit.validate,
    });
  });

  const companyInfoRegistered = {
    ["company-info-registered-address-1"]: addressValidation.line1Validator.when("company-info-company-full-address", {
      is: (value) => !!value,
      then: (schema) => schema,
      otherwise: (schema) => schema.required(requiredFieldText),
    }),
    ["company-info-registered-address-2"]: addressValidation.line2Validator,
    ["company-info-registered-state"]: string().when("company-info-company-full-address", {
      is: (value) => !!value,
      then: (schema) => schema,
      otherwise: () => addressValidation.stateValidator,
    }),
    ["company-info-registered-city"]: addressValidation.cityValidator.when("company-info-company-full-address", {
      is: (value) => !!value,
      then: (schema) => schema,
      otherwise: (schema) => schema.required(requiredFieldText),
    }),
    ["company-info-registered-zip"]: addressValidation.postcodeValidator,
  };

  const validWebsiteMatcher =
    /^(((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)([\w#]+)?)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?)?$/gm;

  const jurisdictionSpecificFieldsValidation = {
    US: {
      ["company-info-phone-number-region-code"]: string().trim().optional(),
      ["company-info-phone-number"]: string()
        .transform((value) => (value ? value : ""))
        .trim()
        .test(
          "Valid phone number - prefix combination",
          intl.formatMessage({
            id: "kyc: Please enter a valid phone number",
            defaultMessage: "Please enter a valid phone number",
          }),
          async function (value) {
            const phoneRegion = this.parent["company-info-phone-number-region-code"];

            if (!value || !phoneRegion) {
              return false;
            }
            const res = await phoneNumberValidator(value, phoneRegion);
            return res.data.isValid;
          }
        ),
      ["company-info-entity-type"]: string()
        .transform((value) => (value ? value : ""))
        .required(requiredFieldText),
      ["company-info-year-of-incorporation"]: string()
        .transform((value) => (value ? value : ""))
        .required(requiredFieldText)
        .min(
          4,
          intl.formatMessage({
            id: "kyc: Please enter a valid year",
            defaultMessage: "Please enter a valid year",
          })
        )
        .max(
          4,
          intl.formatMessage({
            id: "kyc: Please enter a valid year",
            defaultMessage: "Please enter a valid year",
          })
        ),
      ["company-info-website"]: string()
        .transform((value) => (value ? value : ""))
        .required(requiredFieldText)
        .matches(
          validWebsiteMatcher,
          intl.formatMessage({
            id: "kyc: invalid website url",
            defaultMessage: "Please enter a valid URL",
          })
        ),
    },
  };
  return object().shape<KycValidationSchema>({
    ["company-info-reg-ein"]: identityNumberValidator,
    ["company-info-vat"]: string().when("company-info-no-vat-number", {
      is: true,
      then: (schema) => schema,
      otherwise: () => vatNumberValidator,
    }),
    ["company-info-reg-state"]: addressValidation.stateValidator,
    ["company-info-op-address-1"]: addressValidation.line1Validator.when("company-info-reg-same-address", {
      is: true,
      then: (schema) => schema,
      otherwise: (schema) => schema.required(requiredFieldText),
    }),
    ["company-info-op-address-2"]: addressValidation.line2Validator,
    ["company-info-op-state"]: string().when("company-info-reg-same-address", {
      is: true,
      then: (schema) => schema,
      otherwise: () => addressValidation.stateValidator,
    }),
    ["company-info-op-city"]: addressValidation.cityValidator.when("company-info-reg-same-address", {
      is: true,
      then: (schema) => schema,
      otherwise: (schema) => schema.required(requiredFieldText),
    }),
    ["company-info-op-zip"]: string().when("company-info-reg-same-address", {
      is: true,
      then: (schema) => schema,
      otherwise: () => addressValidation.postcodeValidator,
    }),
    ...(jurisdictionSpecificFieldsValidation[country] || {}),
    ...companyInfoRegistered,
  });
};

export const personValidator = (
  intl: IntlShape,
  country: KycSupportedCountry,
  phoneNumberValidator: PhoneNumberValidator
): ObjectSchema<PersonValidationSchema> => {
  const addressValidation = getAddressValidation(intl);

  const requiredFieldText: string = intl.formatMessage({
    id: "kyc: Required field",
    defaultMessage: "Required field",
  });

  const firstNameValidator = string().required(requiredFieldText);
  const middleNameValidator = string();
  const lastNameValidator = string().required(requiredFieldText);
  const roleValidator = mixed().required(requiredFieldText);
  const isUboValidator = mixed().nullable().required(requiredFieldText);
  const emailValidator = string()
    .email(
      intl.formatMessage({
        id: "kyc: Please enter a valid email",
        defaultMessage: "Please enter a valid email",
      })
    )
    .required(requiredFieldText);
  const dobValidator = string()
    .required(requiredFieldText)
    .transform((value) => (value ? moment(value, localDateFormat).format("YYYY-MM-DD") : value))
    .test({
      name: "isValidIsoDate",
      message: intl.formatMessage(
        {
          id: "kyc: Please enter a valid date in the format",
          defaultMessage: "Please enter a valid date in the format {localDateFormatLowerCase}",
        },
        { localDateFormatLowerCase }
      ),
      test: isValidIsoDate,
    })
    .test({
      name: "isValidDOB",
      message: intl.formatMessage({
        id: "Must be over 16 years of age",
        defaultMessage: "Must be over 16 years of age",
      }),
      test: isOver16YearsOfAge,
    })
    .test({
      name: "isAlive",
      message: intl.formatMessage({
        id: "Please enter a valid date of birth",
        defaultMessage: "Please enter a valid date of birth",
      }),
      test: isUnder140YearsOfAge,
    });
  const birthCountryValidator = string()
    .nullable()
    .when("$kycConfiguration", ([kycConfiguration], schema) => {
      if (!kycConfiguration) {
        return schema;
      }
      if (
        kycConfiguration.associatedPersonPlaceOfBirth === false ||
        !kycConfiguration.associatedPersonPlaceOfBirth.countryOfBirth?.required
      ) {
        return schema;
      }
      return schema.required(requiredFieldText);
    });
  const birthCityValidator = string()
    .nullable()
    .when("$kycConfiguration", ([kycConfiguration], schema) => {
      if (!kycConfiguration) {
        return schema;
      }
      if (
        kycConfiguration.associatedPersonPlaceOfBirth === false ||
        !kycConfiguration.associatedPersonPlaceOfBirth.cityOfBirth?.required
      ) {
        return schema;
      }
      return schema.required(requiredFieldText);
    });
  const ssnValidator = string().when(["person-no-ssn", "person-tax-residency", "$kycConfiguration"], {
    is: (noSsn: boolean, taxResidency: string, kycConfiguration: KycConfiguration | undefined) => {
      // No SSN format validation is done if the user has selected that they don't have an SSN or if they are a
      // non-German tax resident completing the German KYC flow
      if (noSsn) {
        return true;
      } else if (!!kycConfiguration && kycConfiguration.country === "DE" && taxResidency !== "DE") {
        return true;
      } else {
        return false;
      }
    },
    then: (schema) =>
      schema.test({
        name: "isNoSsnRequired",
        message: requiredFieldText,
        test: function (value) {
          // For the German KYC flow, the SSN field is still required although no other validation is done
          const kycConfiguration = this.options.context?.kycConfiguration;
          if (!!kycConfiguration && kycConfiguration.country === "DE") {
            if (!value) {
              return false;
            }
          }
          return true;
        },
      }),
    otherwise: (schema) =>
      schema
        .required(requiredFieldText)
        .transform((value) => (typeof value === "string" ? value.toUpperCase().replace(/-/g, "") : value))
        .test({
          name: "isSsnValid",
          test: function (value) {
            // The tax residency field will only be filled out on the German KYC flow
            if (this.parent["person-tax-residency"] === "DE") {
              const isValid = idValidationByCountry["DE"].validate(value);
              return isValid || this.createError({ message: idValidationByCountry["DE"].getMessage(intl) });
            } else {
              const kycConfiguration = this.options.context?.kycConfiguration;
              const countryCode = kycConfiguration.country;
              const isValid = idValidationByCountry[countryCode].validate(value);
              return isValid || this.createError({ message: idValidationByCountry[countryCode].getMessage(intl) });
            }
          },
        }),
  });
  const nationalityValidator = string().required(requiredFieldText);
  const genderValidator = string().required(requiredFieldText);
  const taxResidencyCountryValidator = string().when("$kycConfiguration", {
    is: (kycConfiguration: KycConfiguration): boolean => !!kycConfiguration && kycConfiguration.country === "DE",
    then: (schema) => schema.required(requiredFieldText),
    otherwise: (schema) => schema,
  });
  const uboOwnershipPercentageValidatorForDE = mixed().when("person-is-ubo", {
    is: true,
    then: () =>
      number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required(requiredFieldText)
        .test(
          "min",
          intl.formatMessage({
            id: "kyc: The number must be greater than 25%",
            defaultMessage: "The number must be greater than 25%",
          }),
          (val) => val > 25
        )
        .test(
          "max",
          intl.formatMessage({
            id: "kyc: The number must not be greater than 100%",
            defaultMessage: "The number must not be greater than 100%",
          }),
          (val) => val <= 100
        )
        .test(
          "decimal-places",
          intl.formatMessage({
            id: "kyc: The number cannot have more than 3 decimal places",
            defaultMessage: "The number cannot have more than 3 decimal places",
          }),
          (val) => !!val && (val % 1 === 0 || (val.toString().split(".")[1]?.length ?? 0) <= 3)
        ),
    otherwise: (schema) => schema.transform(() => null).nullable(),
  });
  const uboOwnershipPercentageValidator = mixed().when("person-is-ubo", {
    is: true,
    then: () =>
      number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required(requiredFieldText)
        .test(
          "min",
          intl.formatMessage({
            id: "kyc: The number must be greater than or equal to 25%",
            defaultMessage: "The number must be greater than or equal to 25%",
          }),
          (val) => val >= 25
        )
        .test(
          "max",
          intl.formatMessage({
            id: "kyc: The number must not be greater than 100%",
            defaultMessage: "The number must not be greater than 100%",
          }),
          (val) => val <= 100
        ),
    otherwise: (schema) => schema.transform(() => null).nullable(),
  });
  const signerSubRoleValidator = array().required(requiredFieldText).min(1, requiredFieldText).nullable();

  const jurisdictionSpecificFieldsValidation = {
    DE: {
      ["person-nationality"]: nationalityValidator,
      ["person-gender"]: genderValidator,
      ["person-tax-residency"]: taxResidencyCountryValidator,
      ["person-ubo-ownership-percentage"]: uboOwnershipPercentageValidatorForDE,
    },
    FR: {
      ["person-nationality"]: nationalityValidator,
    },
    AU: {
      ["signer-sub-role"]: signerSubRoleValidator,
    },
    US: {
      ["person-address-state"]: addressValidation.stateValidator.required(requiredFieldText),
      ["person-ubo-ownership-percentage"]: uboOwnershipPercentageValidator,
      ["person-phone-number-region-code"]: string().trim().optional(),
      ["person-phone-number"]: string()
        .trim()
        .test(
          "Valid phone number - prefix combination",
          intl.formatMessage({
            id: "kyc: Please enter a valid phone number",
            defaultMessage: "Please enter a valid phone number",
          }),
          async function (value) {
            const phoneRegion = this.parent["person-phone-number-region-code"];
            if (!value || !phoneRegion) {
              return false;
            }
            const res = await phoneNumberValidator(value, phoneRegion);
            return res.data.isValid;
          }
        ),
    },
  };

  return object().shape<PersonValidationSchema>({
    ["person-first-name"]: firstNameValidator,
    ["person-middle-name"]: middleNameValidator,
    ["person-last-name"]: lastNameValidator,
    ["person-role"]: roleValidator,
    ["person-email"]: emailValidator,
    ["person-dob"]: dobValidator,
    ["person-is-ubo"]: isUboValidator,
    ["person-ssn"]: ssnValidator,
    ["person-birth-country"]: birthCountryValidator,
    ["person-birth-city"]: birthCityValidator,
    ["person-address-line1"]: addressValidation.line1Validator.required(requiredFieldText),
    ["person-address-line2"]: addressValidation.line2Validator,
    ["person-address-state"]: addressValidation.stateValidator,
    ["person-address-city"]: addressValidation.cityValidator.required(requiredFieldText),
    ["person-address-zip"]: getPostcodeValidation(intl, "person-address-country"),
    ["person-address-country"]: addressValidation.countryValidator,
    ...(jurisdictionSpecificFieldsValidation[country] || {}),
  });
};

export const useKycValidation = (
  country: KycSupportedCountry,
  dataValidationUrl: string = STAFF_APP_USER_DATA_VALIDATION_URL
): ObjectSchema<KycValidationSchema> => {
  const intl = useIntl();
  const phoneValidator = useUserDataValidation({ url: dataValidationUrl });

  const phoneNumberValidator = React.useCallback(
    (number?: string, region?: string) =>
      phoneValidator({
        contactPhoneNumber: number && region ? { number: number, regionCode: region } : undefined,
      }),
    [phoneValidator]
  );

  const cachedValidator = React.useMemo<ObjectSchema<KycValidationSchema>>(
    () => kycValidator(intl, country, phoneNumberValidator),
    [country, intl, phoneNumberValidator]
  );
  return cachedValidator;
};

export const usePersonValidation = (
  country: KycSupportedCountry,
  dataValidationUrl: string = STAFF_APP_USER_DATA_VALIDATION_URL
): ObjectSchema<PersonValidationSchema> => {
  const intl = useIntl();
  const phoneValidator = useUserDataValidation({ url: dataValidationUrl });

  const phoneNumberValidator = React.useCallback(
    (number?: string, region?: string) =>
      phoneValidator({
        contactPhoneNumber: number && region ? { number: number, regionCode: region } : undefined,
      }),
    [phoneValidator]
  );

  const cachedValidator = React.useMemo<ObjectSchema<PersonValidationSchema>>(
    () => personValidator(intl, country, phoneNumberValidator),
    [intl, country, phoneNumberValidator]
  );
  return cachedValidator;
};

export const useContractSignerValidation = (country: KycSupportedCountry): SignerValidationByCountry => {
  const cachedValidator = React.useMemo(() => {
    return signerValidationByCountry[country];
  }, [country]);

  return (
    cachedValidator || {
      validate: () => ({ isValid: true }),
    }
  );
};
